'use client';

import type { i18n, TFunction } from 'i18next';
import type { CompanyLegalStatus, InsolvencyRecordItem } from '@/lib/backend-api';
import type { CompanyCountryCode } from '@/lib/company-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InsolvencyStatusBadge } from './InsolvencyStatusBadge';

interface ProfileStatusBadgesProps {
  isDeleted: boolean | undefined;
  legalStatus: CompanyLegalStatus;
  countryCode: CompanyCountryCode;
  insolvencyRecords: InsolvencyRecordItem[] | undefined;
  t: TFunction;
  i18n: i18n;
}

/** The profile header's status chips: Deleted / Insolvent / In Liquidation /
 *  Dissolved, each behind its own `|` separator. */
export function ProfileStatusBadges({
  isDeleted,
  legalStatus,
  countryCode,
  insolvencyRecords,
  t,
  i18n,
}: ProfileStatusBadgesProps) {
  return (
    <>
      {isDeleted && (
        <>
          <span className="text-muted-foreground/50 text-sm leading-none select-none">
            |
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center rounded-full bg-destructive/10 border border-destructive/30 px-1.5 py-0.5 font-medium text-destructive cursor-default">
                  {t('companyDetail.deletedLabel', 'Deleted')}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t(
                  countryCode === 'AT'
                    ? 'companyDetail.deletedTooltipAustria'
                    : 'companyDetail.deletedTooltip',
                  'This company has been deleted from the Handelsregister.',
                )}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
      {legalStatus === 'insolvent' && (
        <>
          <span className="text-muted-foreground/50 text-sm leading-none select-none">
            |
          </span>
          <InsolvencyStatusBadge
            i18n={i18n}
            insolvencyRecords={insolvencyRecords}
            t={t}
          />
        </>
      )}
      {legalStatus === 'in_liquidation' && (
        <>
          <span className="text-muted-foreground/50 text-sm leading-none select-none">
            |
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center rounded-full bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5 font-medium text-orange-600 dark:text-orange-400 cursor-default">
                  {t('companyDetail.inLiquidationLabel', 'In Liquidation')}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {i18n.language === 'de'
                  ? 'Die Gesellschaft ist aufgelöst und befindet sich in Liquidation.'
                  : 'This company is dissolved and currently in liquidation.'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
      {/* Auflösung on record but no Liquidator in office — the
          winding-up is over (or a terminated insolvency left the
          company dissolved). Distinct from "In Liquidation", which
          means the process is still running. */}
      {legalStatus === 'dissolved' && (
        <>
          <span className="text-muted-foreground/50 text-sm leading-none select-none">
            |
          </span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex items-center rounded-full bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 font-medium text-amber-700 dark:text-amber-400 cursor-default">
                  {t('companyDetail.dissolvedLabel', 'Dissolved')}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {i18n.language === 'de'
                  ? 'Die Gesellschaft ist aufgelöst. Es ist kein Liquidator mehr bestellt.'
                  : 'This company is dissolved. No liquidator is currently in office.'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
    </>
  );
}
